import { UseFormReturn, useForm, useNavigate } from '@fukuri-admins/hooks/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { zodResolver } from '@hookform/resolvers/zod';
import { getGroupLink } from '~/utils/getLinks';
import { CreateGroupFormFieldValues, createGroupSchema } from '../logics/schema';
import { CreateGroupFormPresentation } from '../presentations';
import { usePostPartnerAdminsGroups } from './usePostPartnerAdminsGroups';

type PresentationProps = React.ComponentProps<typeof CreateGroupFormPresentation>;

type IUseCreateGroupForm = () => {
  state: {
    methods: UseFormReturn<CreateGroupFormFieldValues>;
    isMutating: boolean;
  };
  actions: {
    handleOnSubmit: PresentationProps['onSubmit'];
  };
};

/**
 * グループ作成フォームのカスタムフック
 *
 * @returns state.methods - react-hook-formのメソッド
 * @returns state.isMutating - API実行中フラグ
 * @returns actions.handleOnSubmit - フォーム送信ハンドラ
 */
export const useCreateGroupForm: IUseCreateGroupForm = () => {
  const threeLetterCode = getThreeLetterCode();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const methods = useForm<CreateGroupFormFieldValues>({
    defaultValues: {
      name: '',
      groupId: '',
      description: '',
    },
    criteriaMode: 'all',
    mode: 'onBlur',
    resolver: zodResolver(createGroupSchema),
  });

  const { trigger, isMutating } = usePostPartnerAdminsGroups(threeLetterCode, {
    onSuccess: ({ group: { id, name } }) => {
      showToast({
        message: `「${name}」を作成しました。`,
        status: 'success',
      });
      navigate(getGroupLink(id));
    },
    onError: error => {
      if (error.response?.status === 422) {
        const errors = error.response?.data?.errors;
        showToast({
          message: errors?.[0] ?? 'グループの作成に失敗しました。',
          status: 'danger',
        });
        return;
      }

      showToast({
        message: 'グループの作成に失敗しました。',
        status: 'danger',
      });
    },
  });

  const handleOnSubmit = methods.handleSubmit(async ({ name, groupId, description }) => {
    await trigger({
      group: {
        name,
        code: groupId,
        description,
      },
    });
  });

  return {
    state: { methods, isMutating },
    actions: { handleOnSubmit },
  };
};
